import classNames from "classnames";
import { OverlayTrigger, Popover } from "react-bootstrap";

import { errorTextClass } from "@/components/ErrorSpan";
import { Icon } from "@/components/Icon";
import { CostTaxBreakdown } from "../queries/tracking-data/cost-tax-breakdown";

export interface CostTaxBreakdownErrorsPopoverProps {
  /**
   * The cost to show the errors for. If there are no errors associated with this cost, nothing will be rendered.
   */
  cost: CostTaxBreakdown;

  /** An optional title to render in the popover */
  title?: React.ReactNode;

  /** The element to render that will trigger the popover. If not provided, a simple error icon will be rendered */
  children?: React.ReactElement;
}

/**
 * Renders an element that shows a popover on hover with the errors associated with a `CostTaxBreakdown`.
 */
export const CostTaxBreakdownErrorsPopover: React.FC<CostTaxBreakdownErrorsPopoverProps> = ({
  cost,
  title,
  children,
}) => {
  if (cost.errors.length === 0) return null;

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={(props) => (
        <Popover
          {...props}
          style={{
            ...props.style,
            "--bs-popover-bg": "var(--bs-warning-bg-subtle)",
          }}
        >
          {title && <Popover.Header as="h3">{title}</Popover.Header>}
          <Popover.Body>
            <p className="mb-1">The following error(s) are associated with this cost:</p>
            <CostTaxBreakdownErrors errors={cost.errors} className="mb-0" />
          </Popover.Body>
        </Popover>
      )}
    >
      {children ?? (
        <span className={errorTextClass}>
          <Icon icon="exclamation-triangle-fill" /> {cost.errors.length} error{cost.errors.length > 1 && "s"}
        </span>
      )}
    </OverlayTrigger>
  );
};

export const CostTaxBreakdownErrors: React.FC<{ errors: CostTaxBreakdown["errors"]; className?: string }> = ({
  errors,
  className,
}) => {
  if (errors.length > 1) {
    // Display errors as a bulleted list
    return (
      <ul className={classNames(errorTextClass, className)}>
        {errors.map((error, i) => (
          <li key={i}>⚠ {error}</li>
        ))}
      </ul>
    );
  } else if (errors.length) {
    // Display a single error
    return <span className={classNames(errorTextClass, className)}>⚠ {errors[0]}</span>;
  } else {
    throw new Error("Cannot render TaxBreakdownErrors component without errors");
  }
};
